/**
 * Cart Custom Fee Mini-Cart Integration
 * Mini购物车抽屉与定制费系统的集成逻辑
 */

class CartCustomFeeMiniCartIntegration {
  
  /**
   * 处理购物车重新渲染时的定制费逻辑
   * @param {CartDrawer} cartDrawerInstance - CartDrawer实例
   * @param {Event} event - cart:refresh事件
   * @returns {Object} { updated: boolean, html?: string, cartContent?: object }
   */
  static async handleCartRerender(cartDrawerInstance, event) {
    try {
      console.log('[Mini-Cart Integration] 开始处理定制费集成');
      
      // 1. 获取当前购物车内容
      let cartContent = null, html = "";
      
      if (event.detail && event.detail["cart"] && event.detail["cart"]["sections"]) {
        cartContent = event.detail["cart"];
        html = event.detail["cart"]["sections"]["mini-cart"];
      } else {
        const response = await fetch(`${window.themeVariables.routes.cartUrl}?section_id=${cartDrawerInstance.getAttribute("section")}`);
        html = await response.text();
      }
      
      // 2. 检查是否需要处理定制费
      if (!this.shouldProcessCustomFees(cartContent)) {
        console.log('[Mini-Cart Integration] 无需处理定制费');
        return { updated: false };
      }
      
      // 3. 显示loading并处理定制费
      console.log('[Mini-Cart Integration] 检测到定制商品，开始处理定制费');
      this.showMiniCartLoading(cartDrawerInstance);
      
      try {
        const feeProcessResult = await this.processCustomFeesForMiniCart(cartContent);
        
        if (feeProcessResult.hasChanges) {
          console.log('[Mini-Cart Integration] 定制费有变化，重新获取购物车内容');
          
          // 重新获取更新后的购物车内容
          const updatedResponse = await fetch(`${window.themeVariables.routes.cartUrl}?section_id=${cartDrawerInstance.getAttribute("section")}`);
          const updatedHtml = await updatedResponse.text();
          
          return { 
            updated: true, 
            html: updatedHtml, 
            cartContent: feeProcessResult.cartContent 
          };
        }
        
        console.log('[Mini-Cart Integration] 定制费无变化');
        return { updated: false };
        
      } finally {
        // 无论成功还是失败，都要隐藏loading
        this.hideMiniCartLoading();
      }
      
    } catch (error) {
      console.error('[Mini-Cart Integration] 处理定制费时出错:', error);
      this.hideMiniCartLoading(); // 确保出错时也隐藏loading
      return { updated: false };
    }
  }
  
  /**
   * 判断是否需要处理定制费
   * @param {Object} cartContent - 购物车内容
   * @returns {boolean}
   */
  static shouldProcessCustomFees(cartContent) {
    // 检查必需的工艺处理器工厂是否存在
    if (!window.CraftProcessorFactory) {
      console.log('[Mini-Cart Integration] CraftProcessorFactory未找到');
      return false;
    }
    
    // 如果没有购物车内容，通过DOM检查
    if (!cartContent) {
      // 检查当前页面是否有定制商品
      const cartItems = document.querySelectorAll('.line-item');
      for (const item of cartItems) {
        const properties = item.querySelectorAll('.product-item-meta__property li');
        
        for (const prop of properties) {
          if (prop.textContent.includes('_is_custom_product: true')) {
            console.log('[Mini-Cart Integration] 通过DOM检测到定制商品');
            return true;
          }
        }
      }
      return false;
    }
    
    // 检查购物车数据中是否有定制商品
    const hasCustomProducts = cartContent.items?.some(item => 
      item.properties && item.properties._is_custom_product === 'true'
    );
    
    if (hasCustomProducts) {
      console.log('[Mini-Cart Integration] 购物车中发现定制商品:', 
        cartContent.items.filter(item => item.properties && item.properties._is_custom_product === 'true').length + '个'
      );
    }
    
    return hasCustomProducts;
  }
  
  /**
   * 专用于mini-cart的定制费处理逻辑
   * @param {Object} cartContent - 购物车内容
   * @returns {Object} { hasChanges: boolean, cartContent?: object }
   */
  static async processCustomFeesForMiniCart(cartContent) {
    try {
      // 防重复处理标记
      if (this._processing) {
        console.log('[Mini-Cart Integration] 正在处理中，跳过重复调用');
        return { hasChanges: false };
      }
      
      this._processing = true;
      
      console.log('[Mini-Cart Integration] 开始专用的mini-cart定制费处理');
      
      // 1. 获取当前购物车数据
      const cartResponse = await fetch('/cart.js');
      const currentCart = await cartResponse.json();
      
      // 2. 分离定制商品和定制费商品
      const customProducts = [];
      const existingFees = [];
      
      currentCart.items.forEach(item => {
        if (this.isCustomProduct(item)) {
          customProducts.push(item);
        } else if (this.isCustomFeeProduct(item)) {
          existingFees.push(item);
        }
      });
      
      console.log('[Mini-Cart Integration] 商品分析:', {
        总商品数: currentCart.items.length,
        定制商品: customProducts.length,
        现有费用: existingFees.length
      });
      
      if (customProducts.length === 0) {
        console.log('[Mini-Cart Integration] 无定制商品，无需处理');
        return { hasChanges: false };
      }
      
      // 3. 计算需要的定制费
      const requiredFees = await this.calculateRequiredFees(customProducts);
      console.log('[Mini-Cart Integration] 计算出需要的费用项:', requiredFees.length + '个');
      
      // 4. 比较现有费用和需要的费用
      const operations = this.planFeeOperations(existingFees, requiredFees);
      console.log('[Mini-Cart Integration] 计划执行的操作:', {
        删除: operations.toRemove.length,
        添加: operations.toAdd.length
      });
      
      // 5. 如果没有操作需要执行，直接返回
      if (operations.toRemove.length === 0 && operations.toAdd.length === 0) {
        console.log('[Mini-Cart Integration] 定制费已正确，无需变更');
        return { hasChanges: false };
      }
      
      // 6. 执行费用操作
      await this.executeFeeOperations(operations);
      
      // 7. 获取更新后的购物车数据
      const updatedCartResponse = await fetch('/cart.js');
      const updatedCart = await updatedCartResponse.json();
      
      return { 
        hasChanges: true, 
        cartContent: updatedCart 
      };
      
    } catch (error) {
      console.error('[Mini-Cart Integration] 处理定制费失败:', error);
      return { hasChanges: false };
    } finally {
      this._processing = false;
    }
  }
  
  /**
   * 检查是否是定制商品
   */
  static isCustomProduct(item) {
    return item.properties && item.properties._is_custom_product === 'true';
  }
  
  /**
   * 检查是否是定制费商品
   */
  static isCustomFeeProduct(item) {
    // 检查 Setup Fee
    if (window.SETUP_FEE_CONFIG && item.variant_id === window.SETUP_FEE_CONFIG.variantId) {
      return true;
    }
    
    // 检查 Plate Fee (PVC工艺)
    if (window.PLATE_FEE_CONFIG && item.variant_id === window.PLATE_FEE_CONFIG.variantId) {
      return true;
    }
    
    if (item.properties && (item.properties._setup_fee === 'true' || item.properties._plate_fee === 'true')) {
      return true;
    }
    
    // 检查所有已注册的工艺类型
    if (window.CraftProcessorFactory) {
      const processTypes = window.CraftProcessorFactory.getAllProcessTypes();
      for (const processType of processTypes) {
        try {
          const processor = window.CraftProcessorFactory.getProcessor(processType);
          if (processor.isCustomFeeProduct(item)) {
            return true;
          }
        } catch (error) {
          console.log(`[Mini-Cart Integration] 检查工艺类型 ${processType} 时出错:`, error);
          continue;
        }
      }
    }
    
    return false;
  }
  
  /**
   * 计算所有需要的定制费（复用现有逻辑）
   */
  static async calculateRequiredFees(customProducts) {
    console.log('[Mini-Cart Integration] 开始批量计算所有需要的定制费');
    
    // 按工艺类型分组
    const productsByType = this.groupProductsByType(customProducts);
    console.log('[Mini-Cart Integration] 按工艺类型分组的商品:', productsByType);
    
    // 为每种工艺类型委托给对应处理器计算
    const allRequiredFees = [];
    
    for (const [craftType, products] of Object.entries(productsByType)) {
      try {
        console.log(`[Mini-Cart Integration] 委托给工艺处理器计算: ${craftType}, 商品数量: ${products.length}`);
        
        // 获取工艺处理器
        const processor = window.CraftProcessorFactory.getProcessor(craftType);
        
        // 委托给处理器计算
        const feeItems = processor.calculateRequiredFeeItems(products);
        console.log(`[Mini-Cart Integration] 工艺 ${craftType} 返回 ${feeItems.length} 个费用项:`, feeItems);
        
        // 为费用项添加标准化属性
        const standardizedItems = this.standardizeFeeItems(feeItems);
        console.log(`[Mini-Cart Integration] 标准化后的费用项:`, standardizedItems);
        allRequiredFees.push(...standardizedItems);
        
      } catch (error) {
        console.log(`[Mini-Cart Integration] 处理工艺类型 ${craftType} 时出错:`, error);
        continue;
      }
    }
    
    console.log(`[Mini-Cart Integration] 总共收集到 ${allRequiredFees.length} 个费用项:`, allRequiredFees);
    return allRequiredFees;
  }
  
  /**
   * 按工艺类型分组商品（复用现有逻辑）
   */
  static groupProductsByType(customProducts) {
    const productsByType = {};
    
    console.log('[Mini-Cart Integration] 开始按工艺类型分组商品', {
      totalProducts: customProducts.length,
      products: customProducts.map(p => ({
        title: p.title,
        craftType: p.properties._internal_craft_type,
        groupId: p.properties._group_id,
        isCustom: p.properties._is_custom_product
      }))
    });
    
    for (const product of customProducts) {
      const craftType = product.properties._internal_craft_type;
      const draftOrderId = product.properties._draft_order;
      const groupId = product.properties._group_id;
      
      // 基础验证
      if (!craftType) {
        console.warn(`[Mini-Cart Integration] 商品缺少工艺类型，跳过: ${product.title}`);
        continue;
      }
      
      if (!groupId) {
        console.warn(`[Mini-Cart Integration] 商品缺少group_id，跳过: ${product.title}`);
        continue;
      }
      
      // 解析定制面信息
      let customSides = [];
      try {
        const customSidesInternal = product.properties['_custom_sides_internal'];
        const customSidesPublic = product.properties['Custom Sides'];
        
        if (customSidesInternal) {
          customSides = JSON.parse(customSidesInternal);
        } else if (customSidesPublic) {
          if (customSidesPublic.startsWith('[') && customSidesPublic.endsWith(']')) {
            customSides = JSON.parse(customSidesPublic);
          } else if (customSidesPublic.includes(',')) {
            customSides = customSidesPublic.split(',').map(side => side.trim());
          } else {
            customSides = [customSidesPublic.trim()];
          }
        }
        
        // 转换为小写格式
        customSides = customSides.map(side => side.toLowerCase());
        
      } catch (error) {
        console.log(`[Mini-Cart Integration] 解析定制面信息失败:`, error);
        customSides = ['front']; // 默认前面
      }
      
      // 分组
      if (!productsByType[craftType]) {
        productsByType[craftType] = [];
      }
      
      productsByType[craftType].push({
        quantity: product.quantity,
        customSides,
        draftOrderId,
        groupId,
        product: product
      });
    }
    
    return productsByType;
  }
  
  /**
   * 标准化费用项（复用现有逻辑）
   */
  static standardizeFeeItems(feeItems) {
    return feeItems.map(item => {
      const groupId = item.properties._group_id || item.properties._draft_order_id;
      
      // 为每个费用项添加唯一标识key用于比较
      if (item.properties._setup_fee === 'true') {
        item.key = `setup_${groupId}`;
      } else if (item.properties._custom_side) {
        item.key = `custom_${groupId}_${item.properties._custom_side}`;
      } else {
        item.key = `unknown_${Date.now()}_${Math.random()}`;
      }
      
      // 标准化variantId属性
      item.variantId = item.id;
      
      return item;
    });
  }
  
  /**
   * 规划费用操作（复用现有逻辑）
   */
  static planFeeOperations(existingFees, requiredFees) {
    const operations = {
      toRemove: [],
      toAdd: []
    };
    
    // 为现有费用创建比较键
    const existingFeeMap = new Map();
    existingFees.forEach(fee => {
      const groupId = fee.properties._group_id || fee.properties._draft_order_id || fee.properties._draft_order;
      if (!fee.properties || !groupId) return;
      
      let key;
      if (fee.properties._setup_fee === 'true') {
        key = `setup_${groupId}`;
      } else if (fee.properties._custom_side) {
        key = `custom_${groupId}_${fee.properties._custom_side}`;
      } else {
        return;
      }
      
      existingFeeMap.set(key, {
        fee,
        variantId: fee.variant_id,
        quantity: fee.quantity,
        matched: false
      });
    });
    
    // 检查需要的费用
    for (const required of requiredFees) {
      const existing = existingFeeMap.get(required.key);
      
      if (existing) {
        if (existing.variantId === required.variantId && existing.quantity === required.quantity) {
          existing.matched = true;
          console.log(`[Mini-Cart Integration] 费用已正确: ${required.key}`);
        } else {
          operations.toRemove.push(existing.fee);
          operations.toAdd.push(required);
          existing.matched = true;
          console.log(`[Mini-Cart Integration] 费用需要更新: ${required.key}`);
        }
      } else {
        operations.toAdd.push(required);
        console.log(`[Mini-Cart Integration] 需要添加费用: ${required.key}`);
      }
    }
    
    // 删除不再需要的费用
    for (const [key, existing] of existingFeeMap) {
      if (!existing.matched) {
        operations.toRemove.push(existing.fee);
        console.log(`[Mini-Cart Integration] 需要删除费用: ${key}`);
      }
    }
    
    return operations;
  }
  
  /**
   * 执行费用操作（复用现有逻辑）
   */
  static async executeFeeOperations(operations) {
    console.log(`[Mini-Cart Integration] 开始批量执行操作: 删除${operations.toRemove.length}个, 添加${operations.toAdd.length}个`);
    
    // 1. 批量删除
    if (operations.toRemove.length > 0) {
      await this.batchRemoveFromCart(operations.toRemove);
    }
    
    // 2. 批量添加
    if (operations.toAdd.length > 0) {
      await this.batchAddToCart(operations.toAdd);
    }
    
    console.log(`[Mini-Cart Integration] 批量操作完成`);
  }
  
  /**
   * 批量删除商品
   */
  static async batchRemoveFromCart(feesToRemove) {
    console.log(`[Mini-Cart Integration] 批量删除 ${feesToRemove.length} 个费用商品`);
    
    try {
      const updates = {};
      feesToRemove.forEach(fee => {
        updates[fee.key] = 0;
      });
      
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      });
      
      if (response.ok) {
        console.log(`[Mini-Cart Integration] 批量删除成功: ${feesToRemove.length} 个商品`);
        return true;
      } else {
        console.log(`[Mini-Cart Integration] 批量删除失败`);
        return false;
      }
    } catch (error) {
      console.log(`[Mini-Cart Integration] 批量删除出错:`, error);
      return false;
    }
  }
  
  /**
   * 批量添加商品到购物车
   */
  static async batchAddToCart(feesToAdd) {
    console.log(`[Mini-Cart Integration] 批量添加 ${feesToAdd.length} 个费用商品`);
    
    try {
      const items = feesToAdd.map(fee => ({
        id: fee.variantId,
        quantity: fee.quantity,
        properties: fee.properties
      }));
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      });
      
      if (response.ok) {
        console.log(`[Mini-Cart Integration] 批量添加成功: ${feesToAdd.length} 个商品`);
        return true;
      } else {
        console.log(`[Mini-Cart Integration] 批量添加失败，回退到单个添加`);
        return await this.fallbackSingleAdd(feesToAdd);
      }
    } catch (error) {
      console.log(`[Mini-Cart Integration] 批量添加出错，回退到单个添加:`, error);
      return await this.fallbackSingleAdd(feesToAdd);
    }
  }
  
  /**
   * 回退到单个添加
   */
  static async fallbackSingleAdd(feesToAdd) {
    let successCount = 0;
    
    for (const fee of feesToAdd) {
      const success = await this.addToCartWithProperties(fee.variantId, fee.quantity, fee.properties);
      if (success) {
        successCount++;
      }
    }
    
    console.log(`[Mini-Cart Integration] 单个添加完成: ${successCount}/${feesToAdd.length} 成功`);
    return successCount === feesToAdd.length;
  }
  
  /**
   * 添加商品到购物车（带属性）
   */
  static async addToCartWithProperties(variantId, quantity, properties = {}) {
    try {
      const requestBody = {
        items: [{
          id: variantId,
          quantity: quantity,
          properties: properties
        }]
      };
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      
      if (response.ok) {
        return true;
      } else {
        console.log(`[Mini-Cart Integration] 添加商品失败: 变体${variantId}`);
        return false;
      }
    } catch (error) {
      console.log(`[Mini-Cart Integration] 添加商品出错: 变体${variantId}`, error);
      return false;
    }
  }
  
  /**
   * 处理定制费并更新购物车（保留原有方法作为备用）
   * @param {Object} cartContent - 购物车内容
   * @returns {Object} { hasChanges: boolean, cartContent?: object }
   */
  static async processAndUpdateCart(cartContent) {
    // 已被 processCustomFeesForMiniCart 替代，保留作为备用
    return this.processCustomFeesForMiniCart(cartContent);
  }
  
  /**
   * 初始化集成
   */
  static init() {
    console.log('[Mini-Cart Integration] 🚀 Mini-Cart定制费集成已初始化');
    
    // 检查必需的依赖
    if (!window.CraftProcessorFactory) {
      console.warn('[Mini-Cart Integration] ⚠️ CraftProcessorFactory未找到，定制费功能可能无法正常工作');
    }
    
    if (!window.SETUP_FEE_CONFIG) {
      console.warn('[Mini-Cart Integration] ⚠️ SETUP_FEE_CONFIG未找到，Setup Fee功能可能无法正常工作');
    }
  }
  
  /**
   * 显示mini-cart的loading状态
   * @param {Element} cartDrawerInstance - CartDrawer实例元素
   */
  static showMiniCartLoading(cartDrawerInstance) {
    try {
      const cartDrawer = cartDrawerInstance || document.querySelector('cart-drawer');
      if (!cartDrawer) return;
      
      // 查找购物车内容区域
      const cartContent = cartDrawer.querySelector('.cart-drawer__content') || 
                         cartDrawer.querySelector('.drawer__content') || 
                         cartDrawer.querySelector('.mini-cart__content') ||
                         cartDrawer.querySelector('[data-cart-content]');
      
      if (cartContent) {
        // 添加loading遮罩层
        const loadingOverlay = document.createElement('div');
        loadingOverlay.id = 'mini-cart-custom-fee-loading';
        loadingOverlay.style.cssText = `
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          font-size: 14px;
          color: #333;
        `;
        loadingOverlay.innerHTML = `
          <div style="text-align: center;">
            <svg width="24" height="24" viewBox="0 0 24 24" style="margin-bottom: 8px; animation: spin 1s linear infinite;">
              <circle cx="12" cy="12" r="10" stroke="#333" stroke-width="2" fill="none" stroke-dasharray="31.416" stroke-dashoffset="31.416">
                <animate attributeName="stroke-dasharray" dur="2s" values="0 31.416;15.708 15.708;0 31.416" repeatCount="indefinite"/>
                <animate attributeName="stroke-dashoffset" dur="2s" values="0;-15.708;-31.416" repeatCount="indefinite"/>
              </circle>
            </svg>
            <div>Calculating custom fees...</div>
          </div>
          <style>
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          </style>
        `;
        
        // 确保父容器有相对定位
        const originalPosition = cartContent.style.position;
        if (!originalPosition || originalPosition === 'static') {
          cartContent.style.position = 'relative';
          loadingOverlay.dataset.originalPosition = originalPosition || 'static';
        }
        
        cartContent.appendChild(loadingOverlay);
        console.log('[Mini-Cart Integration] Loading状态已显示');
      }
    } catch (error) {
      console.error('[Mini-Cart Integration] 显示loading失败:', error);
    }
  }

  /**
   * 隐藏mini-cart的loading状态
   */
  static hideMiniCartLoading() {
    try {
      const loadingOverlay = document.getElementById('mini-cart-custom-fee-loading');
      if (loadingOverlay) {
        const parent = loadingOverlay.parentElement;
        
        // 恢复父容器的position属性
        if (loadingOverlay.dataset.originalPosition) {
          parent.style.position = loadingOverlay.dataset.originalPosition;
        }
        
        loadingOverlay.remove();
        console.log('[Mini-Cart Integration] Loading状态已隐藏');
      }
    } catch (error) {
      console.error('[Mini-Cart Integration] 隐藏loading失败:', error);
    }
  }
}

// 暴露到全局作用域
window.CartCustomFeeMiniCartIntegration = CartCustomFeeMiniCartIntegration;

// 自动初始化
document.addEventListener('DOMContentLoaded', () => {
  CartCustomFeeMiniCartIntegration.init();
});